import { isValid, parse } from "date-fns";

// Translate a moment-style format string from i18n (e.g. "DD.MM.YYYY")
// to date-fns syntax ("dd.MM.yyyy"). MM stays the same.
export function momentFormatToDateFns(momentFormat: string): string {
  return momentFormat.replace(/Y/g, "y").replace(/D/g, "d");
}

// Locale-canonical display format for dates, in date-fns syntax.
export function localeDisplayFormat(): string {
  return momentFormatToDateFns(I18n.t("js.plugins.Locale.Formats.valid_date"));
}

// Accepted user-input variants, tried in priority order. Strict parsing
// against the user's locale (German month names work because formatDate.ts
// sets the date-fns default locale at document-ready).
export function dateInputFormats(displayFormat: string): string[] {
  // Combine each separator (`.`, `-`, `/`) with the four digit-padding
  // permutations and the two year-widths, plus spelled-out months with
  // optional dot/space.
  const numericFormats: string[] = [];
  for (const sep of [".", "-", "/"]) {
    for (const dayMonth of ["dd" + sep + "MM", "d" + sep + "M"]) {
      numericFormats.push(`${dayMonth}${sep}yyyy`);
      numericFormats.push(`${dayMonth}${sep}yy`);
    }
  }

  const monthNameFormats: string[] = [];
  for (const dayPart of ["dd. ", "d. ", "dd ", "d ", "d."]) {
    for (const monthToken of ["MMMM", "MMM"]) {
      monthNameFormats.push(`${dayPart}${monthToken} yyyy`);
      monthNameFormats.push(`${dayPart}${monthToken} yy`);
    }
  }

  return [
    displayFormat, // locale canonical first
    ...numericFormats,
    ...monthNameFormats,
    "ddMMyyyy", // numeric-keypad shortcut: 26111993 → 26.11.1993
    "yyyy-MM-dd", // ISO paste from databases / spreadsheets
  ];
}

// Capitalise the first letter of each word, so "4. juni 1963" parses with
// the German `MMMM` token (which expects "Juni"). Idempotent on already
// well-cased input.
function normaliseMonthCase(input: string): string {
  return input.replace(
    /(^|[\s.\-/])([a-zäöüß])/giu,
    (_, sep, ch) => sep + ch.toUpperCase(),
  );
}

/**
 * Parse a date string the user typed, accepting a handful of common
 * variants (with/without leading zeros, two-digit year, spelled-out month).
 * Returns null when no format matches.
 *
 * Two-digit years are interpreted in the past century when date-fns would
 * otherwise place them in the future — so "63" → 1963, not 2063. This
 * matches the typical use for birthday-style fields.
 */
export function parseUserDate(input: string): Date | null {
  if (!input) return null;

  const normalised = normaliseMonthCase(input.trim());
  const formats = dateInputFormats(localeDisplayFormat());
  const reference = new Date();

  for (const fmt of formats) {
    const parsed = parse(normalised, fmt, reference);
    if (!isValid(parsed)) continue;

    // date-fns parses `yy` as the literal year (so "63" → year 63), and
    // a `yyyy` format also matches 1- to 3-digit years leniently. We treat
    // any year below 1000 as not-yet-fully-typed:
    //   • 2-digit (year < 100): map to a sensible century — prefer 2000s,
    //     fall back to 1900s if that lands in the future. Typical for
    //     birthday-style fields.
    //   • 3-digit (100 ≤ year < 1000): the user is still typing toward a
    //     4-digit year (e.g. "26.11.19" or "26.11.199"). Skip this format
    //     so we don't commit to an outlier year like 0019 or 0199.
    const year = parsed.getFullYear();
    if (year >= 0 && year < 100) {
      parsed.setFullYear(year + 2000);
      if (parsed > reference) parsed.setFullYear(year + 1900);
    } else if (year < 1000) {
      continue;
    }
    return parsed;
  }
  return null;
}
